
import React, { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { heroAvatars } from "./HeroAvatars";

interface UserAvatarProps {
  userId?: string;
  size?: "sm" | "md" | "lg";
  editable?: boolean;
  fullSize?: boolean;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ 
  userId, 
  size = "md", 
  editable = false,
  fullSize = false
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const { user } = useAuth();

  const currentUserId = userId || user?.id;
  
  useEffect(() => {
    if (currentUserId) {
      fetchUserAvatar();
    }
  }, [currentUserId]);
  
  const fetchUserAvatar = async () => {
    if (!currentUserId) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('avatar')
        .eq('id', currentUserId)
        .single();
      
      if (error) {
        console.error("Error fetching avatar:", error);
        return;
      }
      
      if (data && data.avatar) {
        setSelectedAvatar(data.avatar);
      }
    } catch (error) {
      console.error("Error fetching user avatar:", error);
    }
  };
  
  const sizeClasses = {
    sm: fullSize ? "h-full w-full min-h-8 min-w-8" : "h-8 w-8",
    md: fullSize ? "h-full w-full min-h-10 min-w-10" : "h-10 w-10",
    lg: fullSize ? "h-full w-full min-h-16 min-w-16" : "h-16 w-16"
  };

  const handleOpenDialog = () => {
    if (editable) {
      setIsDialogOpen(true);
    }
  };

  const handleSelectAvatar = async (avatarId: string) => {
    if (!currentUserId) return;
    
    try {
      setSelectedAvatar(avatarId);
      
      // Update the user's avatar in the database
      const { error } = await supabase
        .from('profiles')
        .update({ avatar: avatarId })
        .eq('id', currentUserId);
        
      if (error) throw error;
      
      toast.success("Avatar updated successfully");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error updating avatar:", error);
      toast.error("Failed to update avatar");
    }
  };

  // Get user's initials for the avatar fallback
  const getUserInitials = () => {
    // This would normally fetch from the profile, but we'll use a placeholder
    return currentUserId ? "U" : "G";
  };

  // Find the selected hero avatar
  const selectedHeroAvatar = heroAvatars.find(hero => hero.id === selectedAvatar);

  return (
    <>
      <Avatar 
        className={`${sizeClasses[size]} ${editable ? "cursor-pointer hover:opacity-80" : ""}`}
        onClick={handleOpenDialog}
      >
        {selectedHeroAvatar ? (
          <div className="w-full h-full flex items-center justify-center">
            {selectedHeroAvatar.svg}
          </div>
        ) : (
          <AvatarFallback>{getUserInitials()}</AvatarFallback>
        )}
      </Avatar>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Avatar</DialogTitle>
            <DialogDescription>
              Choose a hero avatar for your profile picture
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-4 gap-4 py-4">
            {heroAvatars.map((hero) => (
              <Card 
                key={hero.id} 
                className={`p-2 cursor-pointer transition-all ${
                  selectedAvatar === hero.id ? "ring-2 ring-primary" : "hover:bg-accent"
                }`}
                onClick={() => handleSelectAvatar(hero.id)}
              >
                <div className="flex flex-col items-center">
                  <div className="h-16 w-16 mb-2">
                    {hero.svg}
                  </div>
                  <span className="text-xs text-center">{hero.name}</span>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserAvatar;
